import { useTranslation } from "react-i18next";
import type { CSSProperties } from "react";
import type { PositionDetail, PositionRequest } from "../../api/structure";
import type { MountState } from "../../api/assets";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { compactAttributeValues } from "../../components/AttributeValuesForm";

/** Shared by the structure page and its modals / sections. */
export type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

/** `parent: null` / `position: null` = the first level (there is no fixed root). */
export type ModalState =
  | { kind: "create"; parent: PositionDetail | null }
  | { kind: "edit"; position: PositionDetail }
  | { kind: "move"; position: PositionDetail }
  | { kind: "copy"; position: PositionDetail }
  | { kind: "mount"; position: PositionDetail }
  | { kind: "dismount"; position: PositionDetail }
  | { kind: "dismountAll"; position: PositionDetail; count: number }
  | { kind: "saveTemplate"; position: PositionDetail }
  | { kind: "applyTemplate"; position: PositionDetail }
  | { kind: "newLayout"; position: PositionDetail };

export const NO_VALUE = "";

/** Sprint 3 contract §7: NONE no dot, EMPTY red, MOUNTED green, TEMPORARY amber. */
const MOUNT_DOT_CLASS: Record<MountState, string | null> = {
  NONE: null,
  EMPTY: "tree-dot is-empty",
  MOUNTED: "tree-dot is-mounted",
  TEMPORARY: "tree-dot is-temporary",
};

function mountStateTitle(t: Translate, n: { mountState: MountState; mountedAssetTag: string | null }): string {
  switch (n.mountState) {
    case "EMPTY": return t("structure.emptySlot", "Prazna pozicija — nema montiranog asseta");
    case "MOUNTED": return t("structure.mountedTitle", "Montiran: {{tag}}", { tag: n.mountedAssetTag ?? "" });
    case "TEMPORARY": return t("structure.temporaryTitle", "Privremena zamena: {{tag}}", { tag: n.mountedAssetTag ?? "" });
    default: return "";
  }
}

export function MountDot({ node }: { node: { mountState: MountState; mountedAssetTag: string | null } }) {
  const { t } = useTranslation();
  const cls = MOUNT_DOT_CLASS[node.mountState];
  if (!cls) return null;
  const title = mountStateTitle(t, node);
  return <span className={cls} title={title} aria-label={title} />;
}

type SlotCounterNode = { mountState: MountState; slotsBelow: number; emptySlotsBelow: number; deletedAt?: string | null };

/**
 * "2/13" on a node without an asset of its own (Strahinja, 2026-10-15): empty asset slots / all
 * asset slots in the whole branch below it, so a missing asset shows before the branch is opened.
 * A red dot and grey numbers, no chip; nothing at all once the branch is complete (2026-10-15).
 */
export function SlotCounter({ node }: { node: SlotCounterNode }) {
  const { t } = useTranslation();
  const empty = node.emptySlotsBelow;
  if (node.mountState !== "NONE" || empty === 0 || node.deletedAt) return null;
  const total = node.slotsBelow;
  const title = t("structure.slotsEmptyTitle", "Prazno {{empty}} od {{total}} pozicija za asset u ovoj grani", { empty, total });
  return (
    <span className="tree-slots" title={title} aria-label={title}>
      <span className="tree-dot is-empty" aria-hidden="true" />
      {empty}/{total}
    </span>
  );
}

/** Whole-branch dismount makes sense wherever there is a branch (kinds are admin-defined, so no kind check). */
export function canDismountAll(detail: PositionDetail): boolean {
  return detail.childCount > 0;
}

export function positionTitle(p: { name: string; sequenceNo: number | null }): string {
  return p.sequenceNo !== null ? `${p.name} #${p.sequenceNo}` : p.name;
}

export function formatLocation(loc: NonNullable<PositionDetail["effectiveLocation"]>): JSX.Element {
  const names = [...loc.path.map((p) => p.name), loc.name];
  return (
    <>
      {names.join(" / ")} <span className="muted" style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{loc.code}</span>
    </>
  );
}

/** Full PUT body from a detail (contract §3: `referenceValues` replaced whole, `parentId` ignored on PUT). */
export function toRequest(d: PositionDetail): PositionRequest {
  return {
    parentId: d.parentId,
    code: d.code,
    name: d.name,
    kind: d.kind,
    expectedTypeId: d.expectedTypeId,
    referenceValues: compactAttributeValues(d.referenceValues),
    locationId: d.locationId,
    drawingCode: d.drawingCode,
    interventionNote: d.interventionNote,
    note: d.note,
    sortOrder: d.sortOrder,
  };
}


/** `kindLabel` = display name of the position's kind, for 400 VALIDATION `expectedTypeId: KIND_DOES_NOT_HOLD_ASSET`. */
export function translateError(t: Translate, ae: ApiError, kindLabel?: string): string {
  if (ae.code === "VALIDATION" && detailsMap(ae)?.expectedTypeId === "KIND_DOES_NOT_HOLD_ASSET") {
    return t("structure.error.kindDoesNotHoldAsset", "Vrsta „{{kind}}“ ne nosi asset, pa nema očekivani tip.", { kind: kindLabel ?? "" });
  }
  switch (ae.code) {
    case "CODE_TAKEN": return t("structure.error.codeTaken", "Šifra je već zauzeta.");
    case "CYCLE": return t("structure.error.cycle", "Cilj je unutar sopstvene grane — izaberite drugu poziciju.");
    case "UNKNOWN_ATTRIBUTE": return t("structure.error.unknownAttribute", "Referentne vrednosti ne odgovaraju očekivanom tipu.");
    default: return formatApiError(ae);
  }
}

export const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
export const noticeBox: CSSProperties = { padding: 10, background: "rgba(185,122,14,.10)", color: "var(--warn)", borderRadius: 6, fontSize: 13 };
export const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
export const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
